import React from 'react';
import { useNavigate } from 'react-router-dom';
import { TrashIcon } from '@heroicons/react/24/outline';
import { Printer, Plus } from 'lucide-react';
import {
  ResponsiveModal,
  ResponsiveModalBody,
  Heading,
  Text,
  Button,
  IconButton,
  Badge,
} from '../ui';
import { useLabelQueue } from '../../hooks/useLabelQueueQueries';
import { useRemoveFromLabelQueue, useClearLabelQueue } from '../../hooks/useLabelQueueMutations';
import { formatScientificName } from '../../lib/plantUtils';

interface LabelQueueModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function LabelQueueModal({ isOpen, onClose }: LabelQueueModalProps) {
  const navigate = useNavigate();
  const { data: queueItems = [], isLoading } = useLabelQueue();
  const removeFromQueueMutation = useRemoveFromLabelQueue();
  const clearQueueMutation = useClearLabelQueue();

  const handleGenerateLabels = () => {
    if (queueItems.length === 0) return;

    const plantIds = queueItems
      .filter(item => item.itemType === 'plant' && item.plant)
      .map(item => item.plant!.id);
    const seedIds = queueItems
      .filter(item => item.itemType === 'seed' && item.seedBatch)
      .map(item => item.seedBatch!.id);

    navigate('/labels', {
      state: {
        preSelectedPlantIds: plantIds,
        preSelectedSeedIds: seedIds,
        selectionContext: 'label-queue',
      },
    });
    onClose();
  };

  const handleRemoveItem = (itemType: 'plant' | 'seed', itemId: string) => {
    // We need to update the mutation to handle both types
    if (itemType === 'plant') {
      removeFromQueueMutation.mutate(itemId);
    } else {
      // For now, we'll use the plant mutation but we need to create a seed version
      // This is a temporary workaround
      removeFromQueueMutation.mutate(itemId);
    }
  };

  const handleClearQueue = () => {
    clearQueueMutation.mutate();
  };

  const handleClose = () => {
    onClose();
  };

  return (
    <ResponsiveModal isOpen={isOpen} onClose={handleClose} size="md">
      <ResponsiveModalBody>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <Heading as="h2" size="lg">
              Label Queue
            </Heading>
            <Badge variant="outline" size="sm">
              {queueItems.length} {queueItems.length === 1 ? 'item' : 'items'}
            </Badge>
          </div>

          {isLoading ? (
            <div className="py-8 text-center">
              <Text className="text-muted-foreground">Loading queue...</Text>
            </div>
          ) : queueItems.length === 0 ? (
            <div className="py-8 text-center space-y-4">
              <div className="flex justify-center">
                <Plus className="h-12 w-12 text-muted-foreground" />
              </div>
              <div className="space-y-2">
                <Text className="text-muted-foreground">Your label queue is empty</Text>
                <Text size="sm" className="text-muted-foreground">
                  Add plants and seeds to your queue from the plant and seed lists, then come back
                  here to generate labels in batches.
                </Text>
              </div>
            </div>
          ) : (
            <>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {queueItems.map(item => {
                  const isPlant = item.itemType === 'plant';
                  const currentItem = isPlant ? item.plant : item.seedBatch;

                  if (!currentItem) return null;

                  const scientificName =
                    isPlant && item.plant ? formatScientificName(item.plant) : null;

                  return (
                    <div
                      key={item.id}
                      className="flex items-center justify-between p-3 border border-border rounded-lg"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <Heading as="h4" size="sm" className="truncate">
                            {currentItem.displayName}
                          </Heading>
                          <Badge variant={isPlant ? 'default' : 'secondary'} size="xs">
                            {isPlant ? 'Plant' : 'Seed'}
                          </Badge>
                          {currentItem.qrCodeId && (
                            <Badge variant="success" size="xs">
                              QR
                            </Badge>
                          )}
                        </div>
                        {scientificName && (
                          <Text size="xs" className="italic text-muted-foreground">
                            {scientificName}
                          </Text>
                        )}
                        {isPlant && item.plant?.source && (
                          <Text size="xs" className="text-muted-foreground">
                            {item.plant.source.name}
                          </Text>
                        )}
                        {!isPlant && item.seedBatch?.source && (
                          <Text size="xs" className="text-muted-foreground">
                            {item.seedBatch.source.name}
                          </Text>
                        )}
                      </div>

                      <IconButton
                        icon={<TrashIcon className="h-4 w-4" />}
                        onClick={() => handleRemoveItem(item.itemType, currentItem.id)}
                        variant="ghost"
                        size="sm"
                        aria-label="Remove from queue"
                        disabled={removeFromQueueMutation.isPending}
                      />
                    </div>
                  );
                })}
              </div>

              <div className="flex gap-2 pt-4 border-t border-border">
                <Button
                  onClick={handleGenerateLabels}
                  className="flex-1 flex items-center gap-2"
                  disabled={queueItems.length === 0}
                >
                  <Printer className="h-4 w-4" />
                  Generate Labels ({queueItems.length})
                </Button>

                <Button
                  onClick={handleClearQueue}
                  variant="ghost"
                  disabled={queueItems.length === 0 || clearQueueMutation.isPending}
                >
                  Clear All
                </Button>
              </div>
            </>
          )}

          <div className="flex justify-end pt-4 border-t border-border">
            <Button onClick={handleClose} variant="ghost">
              Close
            </Button>
          </div>
        </div>
      </ResponsiveModalBody>
    </ResponsiveModal>
  );
}
